import { lazy, Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from "react-router-dom";

import { api } from "./api/client";
import { useMe } from "./api/hooks";
import { Shell } from "./components/Shell";
import { Spinner } from "./components/ui/Spinner";
import { useI18n } from "./i18n";
import { LoginPage } from "./pages/Login";
import { NotFoundPage } from "./pages/NotFound";
import { OverviewPage } from "./pages/Overview";
import { ReviewPage } from "./pages/Review";

const TelegramPage = lazy(() => import("./pages/Telegram"));
const AISettingsPage = lazy(() => import("./pages/AISettings"));
const SchedulesPage = lazy(() => import("./pages/Schedules"));
const PlatformsPage = lazy(() => import("./pages/Platforms"));
const PublishLogPage = lazy(() => import("./pages/PublishLog"));
const NotificationsPage = lazy(() => import("./pages/Notifications"));

function FullPageSpinner() {
  const { t } = useI18n();
  return (
    <div className="grid min-h-[50dvh] place-items-center text-muted">
      <Spinner className="size-5" label={t("common.loading")} />
    </div>
  );
}

function RequireAuth() {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) return <FullPageSpinner />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

function RequireAdmin() {
  const { data: me } = useMe();
  if (!me?.is_admin) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Outlet />
    </Suspense>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Shell />,
        children: [
          { path: "/", element: <OverviewPage /> },
          { path: "/review", element: <ReviewPage /> },
          { path: "/review/:id", element: <ReviewPage /> },
          {
            element: <RequireAdmin />,
            children: [
              { path: "/telegram", element: <TelegramPage /> },
              { path: "/ai-settings", element: <AISettingsPage /> },
              { path: "/schedules", element: <SchedulesPage /> },
              { path: "/platforms", element: <PlatformsPage /> },
              { path: "/publish-log", element: <PublishLogPage /> },
              { path: "/notifications", element: <NotificationsPage /> },
            ],
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  useEffect(() => {
    api("auth/csrf").catch(() => undefined);
  }, []);
  return <RouterProvider router={router} />;
}
